import { useEffect, useState } from 'react';
import { http, platformApi, type TenantId } from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import { humaniseStatus } from '../../lib/labels';

type PTenant = {
  ROWID: TenantId; name: string; legal_name?: string; tenant_type: string;
  status: string; trial_ends_at?: string; CREATEDTIME?: string; onboarding_complete: boolean;
};

export default function PlatformTenants() {
  const [tenants, setTenants] = useState<PTenant[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { loginWithToken } = useAuth();

  useEffect(() => {
    http.get<{ tenants: PTenant[] }>('/platform/tenants').then((r) => setTenants(r.tenants)).catch(() => null);
  }, []);

  // Swap the current session for an impersonation session scoped to this tenant.
  // The platform admin keeps their identity (audit trail intact); only the
  // active tenant context changes. Land on the tenant dashboard.
  const impersonate = async (t: PTenant) => {
    if (!confirm(`Impersonate "${t.name}"? You will browse as a super-admin of this tenant. Every action is audit-logged.`)) return;
    setBusy(String(t.ROWID));
    setError(null);
    try {
      const r = await platformApi.impersonate(t.ROWID);
      await loginWithToken(r.token, r.tenant.id);
      window.location.assign('/app/');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const seedCourtCalendar = async () => {
    setBusy('seed-cc'); setError(null);
    try {
      const r = await platformApi.seedCourtCalendar();
      alert(r.inserted > 0
        ? `Inserted ${r.inserted} new court-calendar dates (${r.already_present} already present, ${r.total} total).`
        : `Court calendar already up to date — ${r.already_present} dates on file.`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  // Idempotently insert any matter types from the canonical seed that are
  // missing from the live matter_types table. Purely additive — existing rows
  // are not touched. Run this FIRST when adding new matter types so that the
  // subsequent stage / role re-seeds find ROWIDs to point at.
  const seedMatterTypes = async () => {
    if (!confirm(
      'Insert any missing matter types (ZA) from the canonical seed?\n\n' +
      'Also re-activates any rows that are sitting in the DB with active=NULL ' +
      '(invisible to the dropdown — usually from older seed runs that omitted the flag).'
    )) return;
    setBusy('seed-types'); setError(null);
    try {
      const r = await platformApi.seedMatterTypes();
      alert(
        `Matter types seeded:\n` +
        `  • ${r.inserted} new rows inserted\n` +
        `  • ${r.already_present} already present\n` +
        `  • ${r.reactivated ?? 0} reactivated (active flag repaired)\n` +
        `  • ${r.total} total ZA matter types in DB\n` +
        (r.inserted_codes.length ? `  • New: ${r.inserted_codes.join(', ')}\n` : '') +
        ((r.reactivated_codes?.length ?? 0) ? `  • Reactivated: ${r.reactivated_codes!.join(', ')}` : '')
      );
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  // Re-insert procedural_stages against live matter_types ROWIDs. Same
  // BIGINT-precision failure mode as party roles — stages ended up pointing
  // at deleted matter_type_ids, so stage playbook returned empty. Run AFTER
  // seedMatterTypes (so new matter types have ROWIDs) and BEFORE
  // seedStageTemplates (which needs stages to exist). Idempotent.
  const seedProceduralStages = async () => {
    if (!confirm('Wipe and re-seed procedural_stages (ZA) against live matter_types? Fixes "no court process" on Civil Action / Civil Application / etc. Run BEFORE re-seeding stage templates.')) return;
    setBusy('seed-stages'); setError(null);
    try {
      const r = await platformApi.seedProceduralStages();
      alert(
        `Procedural stages re-seeded:\n` +
        `  • ${r.deleted_existing} old rows deleted\n` +
        `  • ${r.inserted} fresh rows inserted\n` +
        `  • ${r.skipped_no_matter_type} skipped (matter type missing)\n` +
        `  • ${r.matter_types_in_db} matter_types in DB`
      );
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  // Re-populate stage_task_templates + stage_deadline_templates after the
  // BIGINT-precision bug in the seeder broke the stage_id FKs. Run AFTER
  // seedProceduralStages. Idempotent.
  const seedStageTemplates = async () => {
    if (!confirm('Re-seed stage_task_templates + stage_deadline_templates against the live procedural_stages ROWIDs? Idempotent — safe to re-run. Run cleanup-duplicate-catalog first.')) return;
    setBusy('seed-templates'); setError(null);
    try {
      const r = await platformApi.seedStageTemplates();
      alert(
        `Templates re-seeded:\n` +
        `  • ${r.tasks_inserted} task templates inserted\n` +
        `  • ${r.deadlines_inserted} deadline templates inserted\n` +
        `  • ${r.skipped_stages} stages skipped (already had templates or no seed match)\n` +
        `  • ${r.stages_in_db} procedural_stages in DB · ${r.matter_types_in_db} matter_types`,
      );
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  // Re-insert party_role_templates against real matter_types ROWIDs.
  // The original seeder lost precision on the BIGINT FK, leaving every
  // role template pointing at a non-existent matter_type → PartiesPanel had
  // no role sections so you couldn't add parties at all. Nukes existing ZA
  // rows and re-inserts fresh ones. Idempotent.
  const seedPartyRoles = async () => {
    if (!confirm('Wipe and re-seed party_role_templates (ZA) against the live matter_types? Fixes "no party roles" on Civil Action / Civil Application / etc.')) return;
    setBusy('seed-roles'); setError(null);
    try {
      const r = await platformApi.seedPartyRoleTemplates();
      alert(
        `Party role templates re-seeded:\n` +
        `  • ${r.deleted_existing} old rows deleted\n` +
        `  • ${r.inserted} fresh rows inserted\n` +
        `  • ${r.skipped_no_matter_type} skipped (matter type missing)\n` +
        `  • ${r.matter_types_in_db} matter_types in DB`
      );
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  // One-shot recovery of tasks / time_entries that the legacy BIGINT-rounding
  // bug orphaned (matter_id stored as Number() instead of digit string, lost
  // precision past 2^53). Walks every tenant; for each orphan finds the matter
  // R where Number(R) === stored value and rewrites the FK back. Idempotent.
  const repairOrphans = async () => {
    if (!confirm('Repair orphaned tasks / time_entries (legacy BIGINT-rounding bug)? Idempotent — second run reports zero repairs.')) return;
    setBusy('repair-orphans'); setError(null);
    try {
      const r = await platformApi.repairOrphanRowids();
      const lines = [
        'Orphan repair complete:',
        '',
        'Time entries:',
        `  • Scanned ${r.time_entries.scanned}`,
        `  • Repaired ${r.time_entries.repaired}`,
        `  • Unrecoverable ${r.time_entries.unrecoverable}`,
        '',
        'Tasks:',
        `  • Scanned ${r.tasks.scanned}`,
        `  • Repaired ${r.tasks.repaired}`,
        `  • Unrecoverable ${r.tasks.unrecoverable}`,
      ];
      if (r.time_entries.sample.length || r.tasks.sample.length) {
        lines.push('', 'Sample repairs:');
        for (const s of [...r.time_entries.sample, ...r.tasks.sample].slice(0, 5)) {
          lines.push(`  • row ${s.row_id}: matter_id ${s.from} → ${s.to}`);
        }
      }
      alert(lines.join('\n'));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  // Higher courts: Constitutional / SCA / High Court divisions / specialised /
  // tribunals / Small Claims. ~30 rows. Reactivates anything sitting with
  // active=NULL (invisible to the picker) on re-runs.
  const seedHigherCourts = async () => {
    if (!confirm(
      'Seed the higher courts (Constitutional / SCA / High Court divisions / specialised / tribunals / Small Claims)?\n\n' +
      'Idempotent — existing codes are reactivated if their `active` flag is missing, otherwise left alone.'
    )) return;
    setBusy('seed-higher'); setError(null);
    try {
      const r = await platformApi.seedHigherCourts();
      alert(
        `Higher courts seeded:\n` +
        `  • ${r.inserted} new rows inserted\n` +
        `  • ${r.reactivated} reactivated (active flag repaired)\n` +
        `  • ${r.unchanged} already active\n` +
        `  • ${r.total} rows in seed file\n` +
        ((r.sample_inserted_codes?.length ?? 0) ? `\n  Sample new: ${r.sample_inserted_codes.slice(0, 5).join(', ')}…` : '') +
        ((r.sample_reactivated_codes?.length ?? 0) ? `\n  Sample reactivated: ${r.sample_reactivated_codes.slice(0, 5).join(', ')}…` : '')
      );
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  // Idempotent seed of the 509-row SA Magistrates'/Branch/Detached/Periodical
  // courts list from the OCJ register. Existing court codes are skipped so
  // High Court / SCA / specialised / regional entries already there are
  // never touched.
  const seedLowerCourts = async () => {
    if (!confirm(
      'Seed the 509-row SA lower-courts list (Magistrate / Branch / Detached / Periodical) into the courts table?\n\n' +
      'This run will ALSO:\n' +
      '  • delete the 14 deprecated ZA-RMC-* / ZA-DMC-* placeholder rows\n' +
      '  • re-tier any existing magistrate court rows onto magistrate_court\n' +
      '  • populate the new magisterial_district / sub_district / EQC / SCC columns\n\n' +
      'Idempotent — safe to re-run. Existing matters that pointed at the 14 deleted placeholders will need their court re-picked from the new dropdown.'
    )) return;
    setBusy('seed-lower'); setError(null);
    try {
      const r = await platformApi.seedLowerCourts();
      alert(
        `Lower courts seeded:\n` +
        `  • ${r.placeholders_deleted ?? 0} placeholder rows deleted (deprecated ZA-RMC-* / ZA-DMC-*)\n` +
        `  • ${r.inserted} new rows inserted\n` +
        `  • ${r.updated} existing rows updated\n` +
        `  • ${r.unchanged} already correct\n` +
        `  • ${r.total} rows in seed file\n` +
        ((r.sample_inserted_codes?.length ?? 0) ? `\n  Sample new: ${r.sample_inserted_codes.slice(0, 3).join(', ')}…` : '') +
        ((r.sample_updated_codes?.length  ?? 0) ? `\n  Sample updated: ${r.sample_updated_codes.slice(0, 3).join(', ')}…`  : '')
      );
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  // Idempotent seed of the 43-type interlocutory catalogue + each type's
  // stage flow + type-level deadlines. PRECONDITION: the three Catalyst
  // tables (interlocutory_types, interlocutory_stage_templates,
  // interlocutory_deadline_templates) must already exist (DEPLOY.md §3).
  const seedInterlocutoryTypes = async () => {
    if (!confirm('Seed the 43-type interlocutory catalogue (master §10.4) into Catalyst? Idempotent — existing rows are skipped. Requires the three interlocutory_* tables to exist in Catalyst Console.')) return;
    setBusy('seed-interloc'); setError(null);
    try {
      const r = await platformApi.seedInterlocutoryTypes();
      alert(
        `Interlocutory catalogue seeded:\n` +
        `  • ${r.types_inserted} of ${r.types_in_seed} types inserted (rest already present)\n` +
        `  • ${r.stages_inserted} stage-flow rows inserted\n` +
        `  • ${r.deadlines_inserted} type-level deadlines inserted\n` +
        `  • ${r.types_in_db} types now in DB`
      );
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  // Re-seeding the catalog over multiple phases triplicated procedural_stages
  // and party_role_templates. This button collapses them down — keeps the
  // surviving row that has the most stage_task_templates pointing at it,
  // remaps any matter.current_stage_id pointing at deleted duplicates, and
  // drops orphaned templates. Idempotent.
  const cleanupCatalog = async () => {
    if (!confirm('Dedupe duplicate procedural_stages + party_role_templates rows and repoint any affected matters? Idempotent — safe if run more than once.')) return;
    setBusy('cleanup'); setError(null);
    try {
      const r = await platformApi.cleanupDuplicateCatalog();
      alert(
        `Cleanup complete:\n` +
        `  • Procedural stages: ${r.stages.dropped} dropped, ${r.stages.kept} kept\n` +
        `  • Party role templates: ${r.party_roles.dropped} dropped, ${r.party_roles.kept} kept\n` +
        `  • Orphan task/deadline templates removed: ${r.orphan_templates}\n` +
        `  • Matters repointed: ${r.matters_repointed}`
      );
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="stack">
      <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
        <h1 className="grow" style={{ flexBasis: '100%' }}>All tenants</h1>
        <button onClick={seedMatterTypes} disabled={busy === 'seed-types'} title="Step 1 — idempotently insert any missing matter types (ZA) from the canonical seed (additive only)">
          {busy === 'seed-types' ? '…' : '1. Add missing matter types'}
        </button>
        <button onClick={seedProceduralStages} disabled={busy === 'seed-stages'} title="Step 2 — wipe and re-seed procedural_stages (ZA) against live matter_types">
          {busy === 'seed-stages' ? '…' : '2. Re-seed procedural stages'}
        </button>
        <button onClick={seedStageTemplates} disabled={busy === 'seed-templates'} title="Step 3 — re-seed stage_task_templates + stage_deadline_templates against live procedural_stages ROWIDs">
          {busy === 'seed-templates' ? '…' : '3. Re-seed stage templates'}
        </button>
        <button onClick={seedPartyRoles} disabled={busy === 'seed-roles'} title="Step 4 — wipe and re-seed party_role_templates (ZA) against live matter_types">
          {busy === 'seed-roles' ? '…' : '4. Re-seed party roles'}
        </button>
        <button onClick={seedInterlocutoryTypes} disabled={busy === 'seed-interloc'} title="Step 5 — seed 43-type interlocutory catalogue (master §10.4). Requires interlocutory_* tables in Catalyst Console first">
          {busy === 'seed-interloc' ? '…' : '5. Seed interlocutory types'}
        </button>
        <button onClick={cleanupCatalog} disabled={busy === 'cleanup'} title="Step 6 — dedupe + drop orphaned task/deadline templates and repoint matters">
          {busy === 'cleanup' ? '…' : '6. Clean up catalog'}
        </button>
        <button onClick={seedCourtCalendar} disabled={busy === 'seed-cc'} title="Top up the SA court calendar (idempotent)">
          {busy === 'seed-cc' ? '…' : 'Seed court calendar'}
        </button>
        <button onClick={seedHigherCourts} disabled={busy === 'seed-higher'} title="Seed Constitutional / SCA / High Court divisions / specialised / tribunals / Small Claims (~30 rows). Idempotent — reactivates rows with active=NULL.">
          {busy === 'seed-higher' ? '…' : 'Seed higher courts'}
        </button>
        <button onClick={seedLowerCourts} disabled={busy === 'seed-lower'} title="Seed 509 SA lower courts (Magistrate / Branch / Detached / Periodical) — additive only">
          {busy === 'seed-lower' ? '…' : 'Seed lower courts'}
        </button>
        <button onClick={repairOrphans} disabled={busy === 'repair-orphans'} title="One-shot recovery of tasks / time_entries orphaned by the legacy BIGINT-rounding bug. Idempotent.">
          {busy === 'repair-orphans' ? '…' : 'Repair orphan rows'}
        </button>
      </div>
      {error && <div style={{ color: 'var(--color-danger)' }}>{error}</div>}
      <div className="card">
        <table>
          <thead>
            <tr>
              <th>Name</th><th>Type</th><th>Status</th><th>Onboarding</th><th>Created</th><th></th>
            </tr>
          </thead>
          <tbody>
            {tenants.map((t) => (
              <tr key={String(t.ROWID)}>
                <td>{t.name}</td>
                <td>{t.tenant_type}</td>
                <td><span className="pill">{humaniseStatus(t.status)}</span></td>
                <td>{t.onboarding_complete ? 'Done' : 'In progress'}</td>
                <td>{String(t.CREATEDTIME || '').slice(0, 10)}</td>
                <td style={{ textAlign: 'right' }}>
                  <button onClick={() => impersonate(t)} disabled={busy === String(t.ROWID)}>
                    {busy === String(t.ROWID) ? '…' : 'Impersonate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
